export const DASHBOARD_TOUR_STEPS = [
  // Step 1: Initial Welcome Message
  {
    id: "welcome",
    selector: "body",
    placement: "center",
    copy: {
      Admin: {
        title: "Welcome to Your Dashboard",
        content:
          "This dashboard gives you a real-time view of your company's lead flow, team performance, and system activity. Let's take a quick tour of the key features.",
      },
      Manager: {
        title: "Welcome to Your Dashboard",
        content:
          "This dashboard shows you what your team is working on right now. Let's explore the key sections that help you track performance and activity.",
      },
      "Sales Rep": {
        title: "Welcome to Your Dashboard",
        content:
          "This is your workspace. Here you can see your numbers, track your leads, and stay on top of what matters. Let's walk through the main features.",
      },
    },
  },

  // Top bar — Help, theme mode, What's New bell
  {
    id: "topbar-help",
    selector: '[data-tour="topbar-help"]',
    placement: "bottom",
    copy: {
      Admin: {
        title: "Help",
        content:
          "Open this menu on any page for guided tours, replay the full app overview, and play a spoken summary of where you are.",
      },
      "Sales Director": {
        title: "Help",
        content:
          "Open this menu on any page for guided tours, replay the full app overview, and play a spoken summary of where you are.",
      },
      Manager: {
        title: "Help",
        content:
          "Open this menu on any page for guided tours, replay the app overview, and listen to a short spoken summary of the current page.",
      },
      "Sales Rep": {
        title: "Help",
        content:
          "Open this menu on any page for guided tours, replay the app overview, and listen to a short spoken summary of the current page.",
      },
    },
  },

  {
    id: "topbar-theme-mode",
    selector: '[data-tour="topbar-theme-mode"]',
    placement: "bottom",
    copy: {
      Admin: {
        title: "Light / dark mode",
        content:
          "Switch between light and dark mode for the whole app. Your choice applies everywhere in the CRM.",
      },
      "Sales Director": {
        title: "Light / dark mode",
        content:
          "Switch between light and dark mode for the whole app. Your choice applies everywhere in the CRM.",
      },
      Manager: {
        title: "Light / dark mode",
        content:
          "Tap to toggle light or dark mode across the app—helpful in the field or in low-light settings.",
      },
      "Sales Rep": {
        title: "Light / dark mode",
        content:
          "Tap to toggle light or dark mode across the app—helpful in the field or in low-light settings.",
      },
    },
  },

  {
    id: "topbar-bell",
    selector: '[data-tour="topbar-bell"]',
    placement: "bottom",
    copy: {
      Admin: {
        title: "What's New",
        content:
          "See recent release notes and product updates. The badge shows unread What's New items—open the bell to read them.",
      },
      "Sales Director": {
        title: "What's New",
        content:
          "See recent release notes and product updates. The badge shows unread What's New items—open the bell to read them.",
      },
      Manager: {
        title: "What's New",
        content:
          "Check release notes and product updates here. A badge appears when there is unread What's New.",
      },
      "Sales Rep": {
        title: "What's New",
        content:
          "Check release notes and product updates here. A badge appears when there is unread What's New.",
      },
    },
  },

  // Step 2: Lead Status Distribution (status bars — current dispositions in scope)
  {
    id: "lead-status-distribution",
    selector: '[data-tour="lead-status-distribution"]',
    placement: "top",
    copy: {
      Admin: {
        title: "Lead Status Distribution",
        content:
          "Each row is a lead status with a bar showing how many leads currently have that disposition in your selected scope. Use it to see today's status mix across the system—not a pipeline funnel, but a snapshot of where leads sit right now.",
      },
      "Sales Director": {
        title: "Lead Status Distribution",
        content:
          "Status bars show how many leads are in each disposition for your current filters. Scan counts and percentages to see the organization's present status mix at a glance.",
      },
      Manager: {
        title: "Lead Status Distribution",
        content:
          "Bars list each lead status and how many leads in your territories currently use it. Filter by territory to compare status mix for different areas.",
      },
      "Sales Rep": {
        title: "Lead Status Distribution",
        content:
          "See how your leads are spread across statuses right now—each bar is a disposition with a count and share of your book. Helpful for spotting heavy clusters in one status.",
      },
    },
  },

  // Step 3: Team / My Performance (line chart over time)
  {
    id: "rep-performance",
    selector: '[data-tour="rep-performance"]',
    placement: "top",
    copy: {
      Admin: {
        title: "Team Performance",
        content:
          "A line chart for the date range you've picked at the top. Three lines show leads added, leads closed, and leads being worked—so you can see how activity moves over time, not who's \"winning.\" Use the rep filter to zoom in on one person.",
      },
      "Sales Director": {
        title: "Team Performance",
        content:
          "Trend lines for leads added, closed, and being worked across your selected dates. Handy for spotting busy stretches or quiet weeks. Filter to one rep when you want a closer look.",
      },
      Manager: {
        title: "Team Performance",
        content:
          "See how lead activity trends for your team: added, closed, and still being worked. Lines reflect the dashboard date filters—use the rep dropdown to focus on one rep's pattern.",
      },
      "Sales Rep": {
        title: "My Performance",
        content:
          "Your own trend lines for leads added, closed, and being worked over the period you've selected above. Watch how your volume shifts day to day or week to week.",
      },
    },
  },

  // Step 4: Notifications
  {
    id: "notifications",
    selector: '[data-tour="notifications"]',
    placement: "top",
    copy: {
      Admin: {
        title: "Notifications",
        content:
          "Stay updated with real-time notifications about system events, territory changes, and important updates. Check here regularly for alerts.",
      },
      Manager: {
        title: "Notifications",
        content:
          "Get notified about territory updates, team activity, and important system messages. This keeps you informed without checking multiple places.",
      },
      "Sales Rep": {
        title: "Notifications",
        content:
          "See important updates and alerts here. You'll get notified about new leads assigned to you, follow-up reminders, and other relevant information.",
      },
    },
  },

  // Step 5: Team Performance Snapshot
  {
    id: "team-performance-snapshot",
    selector: '[data-tour="team-performance-snapshot"]',
    placement: "top",
    copy: {
      Admin: {
        title: "Team Performance Snapshot",
        content:
          "These cards show aggregated metrics across all reps. Use this to get a quick overview of total leads added, closed, being worked, and overall conversion rate.",
      },
      Manager: {
        title: "Team Performance Snapshot",
        content:
          "This snapshot shows your team's combined performance metrics. It gives you a quick view of total activity, closed leads, and conversion rates.",
      },
      "Sales Rep": {
        title: "Performance Snapshot",
        content:
          "This shows your personal performance metrics: leads added, closed, currently being worked, and your conversion rate. Track your progress here.",
      },
    },
  },

  // Step 6: Lead Status Distribution Territory Filter
  {
    id: "lead-status-filter",
    selector: '[data-tour="lead-status-territory-filter"]',
    placement: "bottom",
    copy: {
      Admin: {
        title: "Filter by Territory",
        content:
          "Narrow the status bars to one territory or view all territories. The counts reflect current lead dispositions for that scope only.",
      },
      "Sales Director": {
        title: "Filter by Territory",
        content:
          "Pick a territory to see its current status bar breakdown, or choose all territories for the full picture.",
      },
      Manager: {
        title: "Filter by Territory",
        content:
          "Switch territories to compare how lead counts stack up by status in each area you manage.",
      },
      "Sales Rep": {
        title: "Territory Filter",
        content:
          "If you work multiple territories, filter here to see status counts for one territory at a time.",
      },
    },
  },

  // Step 7: Team Performance rep filter
  {
    id: "rep-performance-filter",
    selector: '[data-tour="rep-performance-filter"]',
    placement: "bottom",
    copy: {
      Admin: {
        title: "Filter by rep",
        content:
          "Show all reps together or pick one name—the chart updates to that person's added, closed, and being-worked lines only.",
      },
      "Sales Director": {
        title: "Filter by rep",
        content:
          "Switch between the full team and a single rep to read their activity lines without the noise of everyone else.",
      },
      Manager: {
        title: "Filter by rep",
        content:
          "Choose a rep to see just their trends, or leave it on all reps for the team-wide picture.",
      },
      "Sales Rep": {
        title: "Rep filter",
        content:
          "Managers and directors use this to focus the chart on one rep. If you see it, your view may already be scoped to you.",
      },
    },
  },

  // --- Main nav sidebar (order matches sidebar) ---

  {
    id: "sidebar-sales-pipeline",
    selector: '[data-tour="sidebar-sales-pipeline"]',
    placement: "right",
    copy: {
      Admin: {
        title: "Sales pipeline",
        content:
          "Track proposals sent, pending, and closed won. Use this page for monthly pipeline health and deals awaiting customer action.",
      },
      Manager: {
        title: "Sales pipeline",
        content:
          "See proposal flow and closed deals for your team. Open this when reviewing monthly pipeline performance.",
      },
      "Sales Rep": {
        title: "Sales pipeline",
        content:
          "View your proposal activity—sent, pending, and wins—for the current period.",
      },
    },
  },

  {
    id: "sidebar-follow-up-intel",
    selector: '[data-tour="sidebar-follow-up-intel"]',
    placement: "right",
    roles: ["Admin", "Sales Director", "Manager"],
    copy: {
      Admin: {
        title: "Follow-Up Intel",
        content:
          "See overdue and at-risk follow-ups in lanes so managers can prioritize who to contact first.",
      },
      "Sales Director": {
        title: "Follow-Up Intel",
        content:
          "Monitor follow-up risk across territories—overdue, upcoming, and at-risk leads.",
      },
      Manager: {
        title: "Follow-Up Intel",
        content:
          "Prioritize team follow-ups from this board—start with overdue and at-risk lanes.",
      },
    },
  },

  {
    id: "sidebar-territories",
    selector: '[data-tour="sidebar-territories"]',
    placement: "right",
    roles: ["Admin", "Sales Director", "Manager"],
    copy: {
      Admin: {
        title: "Territories",
        content:
          "Manage markets, assign managers and reps, and upload leads. A page tour starts when you open Territories.",
      },
      "Sales Director": {
        title: "Territories",
        content:
          "Set up territories and team coverage. A guided tour runs when you open this page.",
      },
      Manager: {
        title: "Territories",
        content:
          "View and manage territory coverage. A tour guides you when you open this page.",
      },
    },
  },

  // Leads Map Sidebar Item
  {
    id: "sidebar-leads-map",
    selector: '[data-tour="sidebar-leads-map"]',
    placement: "right",
    copy: {
      Admin: {
        title: "Leads Map",
        content:
          "Click here to access the interactive map view of all leads. The map shows leads geographically, helps you visualize coverage, and plan routes. A tour will start when you open this page.",
      },
      Manager: {
        title: "Leads Map",
        content:
          "View all leads on an interactive map to see geographic coverage and plan efficient routes. A tour will guide you when you open this page.",
      },
      "Sales Rep": {
        title: "Leads Map",
        content:
          "Open the map view to see all your leads plotted geographically. Use this to plan your daily routes and navigate efficiently. A tour will start when you open this page.",
      },
    },
  },

  {
    id: "sidebar-search-leads",
    selector: '[data-tour="sidebar-search-leads"]',
    placement: "right",
    copy: {
      Admin: {
        title: "Search Leads",
        content:
          "Click here to access the Search Leads page. Use powerful search and filtering to find specific leads across all territories. A tour will start when you open this page.",
      },
      Manager: {
        title: "Search Leads",
        content:
          "Click here to search and filter leads across your territories. Find specific leads quickly using search terms or advanced filters. A tour will guide you when you open this page.",
      },
      "Sales Rep": {
        title: "Search Leads",
        content:
          "Click here to search through your leads. Use the search bar and filters to find specific leads quickly. A tour will start when you open this page.",
      },
    },
  },

  {
    id: "sidebar-live-sms",
    selector: '[data-tour="sidebar-live-sms"]',
    placement: "right",
    copy: {
      Admin: {
        title: "Live SMS",
        content:
          "Two-way texting with leads. Keep customer conversations inside the CRM.",
      },
      Manager: {
        title: "Live SMS",
        content:
          "Read and send SMS with leads from conversation threads.",
      },
      "Sales Rep": {
        title: "Live SMS",
        content:
          "Text leads directly—open a thread to read and reply without leaving the app.",
      },
    },
  },

  {
    id: "sidebar-team-chat",
    selector: '[data-tour="sidebar-team-chat"]',
    placement: "right",
    copy: {
      Admin: {
        title: "Team Chat",
        content:
          "Click here to message your team in channels. Use channels like General, Sales, and Support, and mention colleagues with @. A tour will start when you open this page.",
      },
      Manager: {
        title: "Team Chat",
        content:
          "Click here to chat with your team in channels. Switch between channels and use @ to mention teammates. A tour will guide you when you open this page.",
      },
      "Sales Rep": {
        title: "Team Chat",
        content:
          "Click here to message your team in channels. Use @ to mention teammates so they get notified. A tour will start when you open this page.",
      },
    },
  },

  {
    id: "sidebar-rep-positions",
    selector: '[data-tour="sidebar-rep-positions"]',
    placement: "right",
    roles: ["Admin", "Sales Director", "Manager"],
    copy: {
      Admin: {
        title: "Rep Positions",
        content:
          "A live map of where your reps are in the field. Managers use it to see who's out working and which areas they're covering today.",
      },
      "Sales Director": {
        title: "Rep Positions",
        content:
          "Open the live map to see rep locations in the field—who's working and what parts of the territory they're in.",
      },
      Manager: {
        title: "Rep Positions",
        content:
          "A live map so you can see where your reps are and which areas they're working—helps you stay in sync with the team in the field.",
      },
    },
  },

  {
    id: "sidebar-whats-new",
    selector: '[data-tour="sidebar-whats-new"]',
    placement: "right",
    copy: {
      Admin: {
        title: "What's New",
        content:
          "Release notes and product updates—check here after upgrades to see what changed.",
      },
      Manager: {
        title: "What's New",
        content:
          "See what's new in Territory CRM when we ship updates.",
      },
      "Sales Rep": {
        title: "What's New",
        content:
          "Read release notes when the app is updated with new features.",
      },
    },
  },

  {
    id: "sidebar-settings",
    selector: '[data-tour="sidebar-settings"]',
    placement: "right",
    roles: ["Admin", "Sales Director", "Manager"],
    copy: {
      Admin: {
        title: "Settings",
        content:
          "Expand Settings when you're ready to configure company profile, catalog, users, and integrations. A guided tour runs the first time you open this menu.",
      },
      "Sales Director": {
        title: "Settings",
        content:
          "Expand Settings for company setup, products, users, and reports. Your first expand starts a short settings tour.",
      },
      Manager: {
        title: "Settings",
        content:
          "Expand Settings for reports and exports. A short tour runs the first time you open this menu.",
      },
    },
  },
];